import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Minimal shadcn-style Accordion built on plain React state.
 *
 * @radix-ui/react-accordion is not installed in this project, so this
 * file gives us the same composition surface (Accordion / AccordionItem
 * / AccordionTrigger / AccordionContent) without adding a dep. It only
 * implements what the alert-detail page needs in PR 2:
 *   - single-item, collapsible (default closed)
 *   - chevron rotates on open
 *   - keyboard a11y comes from the underlying <button>
 *
 * If the design system later wants multi-item or controlled mode, swap
 * this for the real Radix package and the call-sites won't change.
 */

interface AccordionContextValue {
  openItem: string | null;
  setOpenItem: (id: string | null) => void;
}

const AccordionContext = React.createContext<AccordionContextValue | null>(null);

interface AccordionProps {
  type?: 'single';
  collapsible?: boolean;
  defaultValue?: string;
  className?: string;
  children: React.ReactNode;
}

export function Accordion({
  defaultValue,
  className,
  children,
}: AccordionProps): JSX.Element {
  const [openItem, setOpenItem] = React.useState<string | null>(
    defaultValue ?? null,
  );
  const value = React.useMemo(
    () => ({ openItem, setOpenItem }),
    [openItem],
  );
  return (
    <AccordionContext.Provider value={value}>
      <div className={cn('w-full', className)}>{children}</div>
    </AccordionContext.Provider>
  );
}

interface AccordionItemContextValue {
  itemId: string;
  isOpen: boolean;
  toggle: () => void;
}

const AccordionItemContext = React.createContext<AccordionItemContextValue | null>(
  null,
);

interface AccordionItemProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

export function AccordionItem({
  value,
  className,
  children,
}: AccordionItemProps): JSX.Element {
  const ctx = React.useContext(AccordionContext);
  if (!ctx) {
    throw new Error('AccordionItem must be used inside an Accordion');
  }
  const isOpen = ctx.openItem === value;
  const toggle = React.useCallback(() => {
    ctx.setOpenItem(isOpen ? null : value);
  }, [ctx, isOpen, value]);
  const itemValue = React.useMemo(
    () => ({ itemId: value, isOpen, toggle }),
    [value, isOpen, toggle],
  );
  return (
    <AccordionItemContext.Provider value={itemValue}>
      <div className={cn('border-b', className)}>{children}</div>
    </AccordionItemContext.Provider>
  );
}

interface AccordionTriggerProps {
  className?: string;
  children: React.ReactNode;
}

export function AccordionTrigger({
  className,
  children,
}: AccordionTriggerProps): JSX.Element {
  const ctx = React.useContext(AccordionItemContext);
  if (!ctx) {
    throw new Error('AccordionTrigger must be used inside an AccordionItem');
  }
  return (
    <button
      type="button"
      onClick={ctx.toggle}
      aria-expanded={ctx.isOpen}
      className={cn(
        'flex w-full items-center justify-between py-4 text-left text-sm font-medium transition-all hover:underline',
        className,
      )}
    >
      <span className="flex-1">{children}</span>
      <ChevronDown
        className={cn(
          'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
          ctx.isOpen && 'rotate-180',
        )}
      />
    </button>
  );
}

interface AccordionContentProps {
  className?: string;
  children: React.ReactNode;
}

export function AccordionContent({
  className,
  children,
}: AccordionContentProps): JSX.Element | null {
  const ctx = React.useContext(AccordionItemContext);
  if (!ctx) {
    throw new Error('AccordionContent must be used inside an AccordionItem');
  }
  if (!ctx.isOpen) return null;
  return (
    <div className={cn('pb-4 pt-0 text-sm', className)}>{children}</div>
  );
}
